/**
 * Wonderbiotics 产品订阅集成 JavaScript
 * 专门为 https://wonderbiotics.com/products/wonderbiotics-easyshape-proboitic 设计
 * 
 * 功能：
 * - 直接显示所有变体的订阅选项
 * - 处理订阅选择逻辑
 * - 集成 Appstle Subscriptions API
 * - 更新价格和购物车按钮
 */

(function() {
  'use strict';

  class WonderbioticsSubscriptions {
    constructor(container) {
      this.container = container;
      this.productId = container.dataset.productId;
      this.productHandle = container.dataset.productHandle;
      
      // DOM 元素
      this.variantCards = container.querySelectorAll('.variant-subscription-card:not(.one-time-purchase-card)');
      this.planRadios = container.querySelectorAll('.plan-radio:not(.one-time-radio)');
      this.variantIdInput = container.querySelector('#wonderbiotics-variant-id');
      this.sellingPlanInput = container.querySelector('#wonderbiotics-selling-plan');
      this.addToCartBtn = container.querySelector('#wonderbiotics-add-to-cart');
      this.cartPriceDisplay = container.querySelector('#wonderbiotics-cart-price');
      
      // 一次性购买相关元素
      this.oneTimeCard = container.querySelector('.one-time-purchase-card');
      this.oneTimeSelector = container.querySelector('.one-time-variant-selector');
      this.oneTimeRadio = container.querySelector('.one-time-radio');
      this.oneTimeInfo = container.querySelector('#selected-variant-info');
      this.oneTimePriceDisplay = container.querySelector('#one-time-price-display');
      
      // Shopify 原生表单和按钮
      this.shopifyForm = this.findShopifyProductForm();
      this.shopifyVariantInput = this.findShopifyVariantInput();
      this.shopifySellingPlanInput = this.findShopifySellingPlanInput();
      this.shopifyAddToCartBtn = this.findShopifyAddToCartButton();
      
      // 状态
      this.selectedVariant = null;
      this.selectedSellingPlan = null;
      this.appstleInitialized = false;
      this.discountRemovedVerified = false;
      this.cartWatchInterval = null;
      this.lastCartCheck = 0;
      this.cartCheckThrottle = 5000; // 5秒内最多检查一次
      
      this.init();
    }

    init() {
      // console.log('初始化 Wonderbiotics 订阅组件');
      
      // 查找 Shopify 原生元素（延迟查找，确保 DOM 已加载）
      setTimeout(() => {
        this.shopifyForm = this.findShopifyProductForm();
        this.shopifyVariantInput = this.findShopifyVariantInput();
        this.shopifySellingPlanInput = this.findShopifySellingPlanInput();
        this.shopifyAddToCartBtn = this.findShopifyAddToCartButton();
        
        // 如果找到原生按钮，可以隐藏我们的按钮或同步样式
        if (this.shopifyAddToCartBtn && this.addToCartBtn) {
          // 可以选择隐藏我们的按钮，使用原生按钮
          // this.addToCartBtn.style.display = 'none';
          // 或者同步点击事件到原生按钮
          // console.log('已找到 Shopify 原生加购按钮，将使用它来添加商品');
        }
      }, 100);
      
      // 绑定事件
      this.bindEvents();
      
      // 拦截表单提交，确保 selling_plan 被包含
      this.interceptFormSubmit();
      
      // 监听购物车更新，检测并移除自动应用的折扣码
      this.watchCartForAutoDiscounts();
      
      // 阻止自动应用折扣码
      this.preventAutoDiscountOnAdd();
      
      // 初始化 Appstle
      this.initAppstle();
      
      // 设置默认选择
      this.setDefaultSelection();
      
      // 监听页面变体选择器
      this.watchPageVariantSelector();
      
      // 从 URL 参数读取变体
      this.readVariantFromURL();
    }
    
    // 拦截表单提交，确保 selling_plan 被包含
    interceptFormSubmit() {
      if (!this.shopifyForm) return;
      
      // 监听表单提交事件
      this.shopifyForm.addEventListener('submit', (e) => {
        // 检查是否正在提交（防止重复提交）
        if (this.shopifyForm.getAttribute('data-submitting') === 'true') {
          // console.warn('表单正在提交中，阻止重复提交');
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        
        // 标记正在提交
        this.shopifyForm.setAttribute('data-submitting', 'true');
        
        // 确保 selling_plan 已设置
        if (this.selectedSellingPlan) {
          // 移除所有重复字段
          this.ensureSellingPlanInForm();
          this.verifySellingPlanBeforeSubmit();
          
          // console.log('表单提交前验证 - selling_plan:', this.selectedSellingPlan);
          
          // 检查表单数据
          const formData = new FormData(this.shopifyForm);
          const sellingPlanValue = formData.get('selling_plan');
          const variantValue = formData.get('id');
          
          // console.log('表单提交数据:', {
          //   variant: variantValue,
          //   selling_plan: sellingPlanValue,
          //   expected_selling_plan: this.selectedSellingPlan
          // });
          
          if (sellingPlanValue !== this.selectedSellingPlan) {
            // console.warn('表单中的 selling_plan 值不正确，正在修复...');
            const sellingPlanInput = this.shopifyForm.querySelector('[name="selling_plan"]');
            if (sellingPlanInput) {
              sellingPlanInput.value = this.selectedSellingPlan;
            }
          }
          
          // 确保没有其他可能影响价格的字段
          this.cleanupFormForSubscription();
        } else {
          // 如果没有选择订阅，确保 selling_plan 字段为空或不存在
          const sellingPlanInput = this.shopifyForm.querySelector('[name="selling_plan"]');
          if (sellingPlanInput) {
            sellingPlanInput.value = '';
          }
        }
        
        // 提交完成后重置状态
        setTimeout(() => {
          this.shopifyForm.removeAttribute('data-submitting');
          this.resetFormState();
        }, 1000);
      }, true); // 使用捕获阶段，确保在其他监听器之前执行
    }
    
    // 清理表单，确保订阅正确传递
    cleanupFormForSubscription() {
      if (!this.shopifyForm) return;
      
      // 移除可能冲突的字段
      const conflictingInputs = this.shopifyForm.querySelectorAll(
        '[name="selling_plan_id"]:not([data-keep]), ' +
        '[name="properties[_selling_plan]"]:not([data-keep])'
      );
      
      conflictingInputs.forEach(input => {
        input.remove();
        // console.log('移除了可能冲突的字段:', input.name);
      });
      
      // 确保只有一个 selling_plan 字段
      const sellingPlanInputs = this.shopifyForm.querySelectorAll('[name="selling_plan"]');
      if (sellingPlanInputs.length > 1) {
        sellingPlanInputs.forEach((input, index) => {
          if (index > 0) {
            input.remove();
            // console.log('移除了重复的 selling_plan 字段');
          }
        });
      }
    }

    bindEvents() {
      // 订阅计划选择
      this.planRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.handlePlanSelection(e.target);
        });
        
        // 也监听 click 事件，确保立即清除其他选中状态
        radio.addEventListener('click', (e) => {
          // 如果这个radio还没被选中，先清除所有其他选中状态
          if (!e.target.checked) {
            this.clearAllSelections();
          }
        });
      });
      
      // 一次性购买卡片点击事件 - 点击整个卡片时打开下拉选择器
      if (this.oneTimeCard) {
        this.oneTimeCard.addEventListener('click', (e) => {
          // 如果点击的是 select 元素或其父元素，不处理（让 select 自己处理）
          if (e.target.closest('.one-time-variant-selector') || 
              e.target.closest('.variant-selector-wrapper')) {
            return;
          }
          
          // 阻止事件冒泡
          e.stopPropagation();
          
          // 触发下拉选择器打开
          this.handleOneTimeCardClick();
        });
        
        // 添加鼠标样式提示
        this.oneTimeCard.style.cursor = 'pointer';
      }
      
      // 一次性购买变体选择器
      if (this.oneTimeSelector) {
        // 阻止 select 的点击事件冒泡到卡片
        this.oneTimeSelector.addEventListener('click', (e) => {
          e.stopPropagation();
        });
        
        // 监听 change 事件
        this.oneTimeSelector.addEventListener('change', (e) => {
          this.handleOneTimeVariantChange(e.target);
        });
        
        // 监听 blur 事件（下拉关闭时），确保即使选择相同的值也能处理
        let previousValue = this.oneTimeSelector.value;
        this.oneTimeSelector.addEventListener('focus', () => {
          previousValue = this.oneTimeSelector.value;
        });
        
        this.oneTimeSelector.addEventListener('blur', (e) => {
          // 下拉关闭时，如果值存在且与之前不同，或者需要确保状态正确
          const currentValue = this.oneTimeSelector.value;
          if (currentValue && currentValue !== '') {
            // 即使值相同，也确保状态正确（处理首次选择默认选项的情况）
            if (currentValue !== previousValue || !this.oneTimeRadio?.checked) {
              this.handleOneTimeVariantChange(e.target);
            }
          }
        });
      }
      
      // 一次性购买单选按钮
      if (this.oneTimeRadio) {
        this.oneTimeRadio.addEventListener('change', (e) => {
          this.handleOneTimeSelection(e.target);
        });
      }
      
      // 添加到购物车按钮（已隐藏，不再使用）
      // 如果用户点击了我们的按钮（虽然已隐藏），仍然触发主题自带按钮
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          // 直接触发主题自带按钮
          if (this.shopifyAddToCartBtn) {
            this.addToCart();
          }
        });
      }
      
      // 监听主题自带按钮的点击，确保数据同步
      if (this.shopifyAddToCartBtn) {
        this.shopifyAddToCartBtn.addEventListener('click', (e) => {
          // 在主题按钮点击前，确保数据已同步
          if (this.selectedVariant) {
            this.syncToShopifyForm(this.selectedVariant, this.selectedSellingPlan);
            this.ensureSellingPlanInForm();
            this.verifySellingPlanBeforeSubmit();
            
            // console.log('主题自带按钮点击，已同步数据:', {
            //   variant: this.selectedVariant,
            //   selling_plan: this.selectedSellingPlan || '(一次性购买)'
            // });
          }
        }, true); // 使用捕获阶段，在其他监听器之前执行
      }
    }
    
    // 处理一次性购买卡片点击 - 打开下拉选择器
    handleOneTimeCardClick() {
      // console.log('一次性购买卡片被点击，打开下拉选择器');
      
      if (!this.oneTimeSelector) {
        // console.warn('无法找到一次性购买选择器');
        return;
      }
      
      // 检查当前是否已经有选中的值
      const currentValue = this.oneTimeSelector.value;
      const currentIndex = this.oneTimeSelector.selectedIndex;
      
      // 如果第一个选项已经被选中，先手动触发一次选择处理
      // 这样即使用户再次选择第一个选项，也能正确响应
      if (currentValue && currentIndex >= 0) {
        // 先触发一次选择处理，确保状态正确
        this.handleOneTimeVariantChange(this.oneTimeSelector);
      }
      
      // 触发下拉选择器打开
      // 使用 setTimeout 确保在事件处理完成后打开
      setTimeout(() => {
        // 方法1: 如果浏览器支持 showPicker() API（现代浏览器）
        if (this.oneTimeSelector.showPicker && typeof this.oneTimeSelector.showPicker === 'function') {
          try {
            this.oneTimeSelector.showPicker();
            return;
          } catch (e) {
            // console.log('showPicker() 不可用，尝试其他方法');
          }
        }
        
        // 方法2: 聚焦并触发点击事件
        this.oneTimeSelector.focus();
        
        // 创建一个鼠标事件来模拟点击
        const mouseEvent = new MouseEvent('mousedown', {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 1
        });
        
        this.oneTimeSelector.dispatchEvent(mouseEvent);
        
        // 也触发 click 事件
        setTimeout(() => {
          const clickEvent = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
            buttons: 1
          });
          this.oneTimeSelector.dispatchEvent(clickEvent);
        }, 10);
      }, 10);
    }
    
    // 处理一次性购买变体选择
    handleOneTimeVariantChange(select) {
      const variantId = select.value;
      const selectedOption = select.options[select.selectedIndex];
      
      if (variantId && selectedOption && variantId !== '') {
        // 获取格式化后的价格（优先使用）
        const formattedPrice = selectedOption.dataset.variantPrice;
        const variantTitle = selectedOption.dataset.variantTitle;
        
        // 更新价格显示
        if (this.oneTimePriceDisplay) {
          if (formattedPrice) {
            // 使用格式化后的价格（已经是 $XX.XX 格式）
            this.oneTimePriceDisplay.textContent = formattedPrice;
          } else {
            // 如果没有格式化价格，使用分单位的价格转换
            const priceCents = selectedOption.dataset.variantPriceCents;
            if (priceCents) {
              const priceNum = parseFloat(priceCents);
              if (!isNaN(priceNum)) {
                // Shopify 价格以分为单位，转换为美元
                const priceInDollars = (priceNum / 100).toFixed(2);
                this.oneTimePriceDisplay.textContent = '$' + priceInDollars;
              }
            }
          }
        }
        
        // 显示变体信息
        if (this.oneTimeInfo) {
          this.oneTimeInfo.style.display = 'block';
        }
        
        // 选中一次性购买选项
        if (this.oneTimeRadio) {
          this.oneTimeRadio.checked = true;
          this.oneTimeRadio.dataset.variantId = variantId;
          this.handleOneTimeSelection(this.oneTimeRadio);
        }
      } else {
        // 如果没有选择，隐藏信息
        if (this.oneTimeInfo) {
          this.oneTimeInfo.style.display = 'none';
        }
        if (this.oneTimeRadio) {
          this.oneTimeRadio.checked = false;
        }
      }
    }
    
    // 处理一次性购买选择
    handleOneTimeSelection(radio) {
      const variantId = radio.dataset.variantId || this.oneTimeSelector?.value;
      
      if (!variantId) {
        return;
      }
      
      // console.log('处理一次性购买选择:', { variantId });
      
      // 清除所有订阅选项的选中状态
      this.clearAllSelections();
      
      // 更新状态
      this.selectedVariant = variantId;
      this.selectedSellingPlan = '';
      
      // 更新隐藏输入
      if (this.variantIdInput) {
        this.variantIdInput.value = variantId;
      }
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }
      
      // 重置表单状态（重要：确保表单可以再次提交）
      this.resetFormState();
      
      // 同步到 Shopify 原生表单
      this.syncToShopifyForm(variantId, '');
      
      // 更新价格显示（购物车按钮）
      const selectedOption = this.oneTimeSelector?.options[this.oneTimeSelector.selectedIndex];
      if (selectedOption && this.cartPriceDisplay) {
        // 优先使用格式化后的价格
        const formattedPrice = selectedOption.dataset.variantPrice;
        if (formattedPrice) {
          this.cartPriceDisplay.textContent = formattedPrice;
        } else {
          // 如果没有格式化价格，使用分单位的价格转换
          const priceCents = selectedOption.dataset.variantPriceCents;
          if (priceCents) {
            const priceNum = parseFloat(priceCents);
            if (!isNaN(priceNum)) {
              // Shopify 价格以分为单位，转换为美元
              const priceInDollars = (priceNum / 100).toFixed(2);
              this.cartPriceDisplay.textContent = '$' + priceInDollars;
            }
          }
        }
      }
      
      // 启用添加到购物车按钮
      this.enableAddToCart();
      
      // 高亮一次性购买卡片
      if (this.oneTimeCard) {
        this.variantCards.forEach(card => {
          card.classList.remove('active-variant');
        });
        this.oneTimeCard.classList.add('active-variant');
      }
      
      // 触发自定义事件
      this.dispatchEvent('subscriptionSelected', {
        variantId: variantId,
        sellingPlanId: ''
      });
      
      // console.log('一次性购买已选择:', { variantId });
    }
    
    // 清除所有选中状态
    clearAllSelections() {
      this.planRadios.forEach(radio => {
        radio.checked = false;
      });
      
      // 也清除一次性购买选项
      if (this.oneTimeRadio) {
        this.oneTimeRadio.checked = false;
      }
      
      this.container.querySelectorAll('.plan-label').forEach(label => {
        label.classList.remove('selected');
      });
      
      this.variantCards.forEach(card => {
        card.classList.remove('active-variant');
      });
      
      // 清除一次性购买卡片的高亮
      if (this.oneTimeCard) {
        this.oneTimeCard.classList.remove('active-variant');
      }
    }

    handlePlanSelection(radio) {
      const variantId = radio.dataset.variantId;
      const sellingPlanId = radio.dataset.sellingPlanId || '';
      
      // console.log('处理订阅选择:', { variantId, sellingPlanId });
      
      // 先清除所有选中状态
      this.clearAllSelections();
      
      // 更新状态
      this.selectedVariant = variantId;
      this.selectedSellingPlan = sellingPlanId;
      
      // 更新我们的隐藏输入
      if (this.variantIdInput) {
        this.variantIdInput.value = variantId;
      }
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = sellingPlanId;
      }
      
      // 重置表单状态（重要：确保表单可以再次提交）
      this.resetFormState();
      
      // 同步到 Shopify 原生表单
      this.syncToShopifyForm(variantId, sellingPlanId);
      
      // 更新视觉反馈
      this.updateVisualFeedback(radio);
      
      // 更新价格显示
      this.updatePriceDisplay(radio);
      
      // 启用添加到购物车按钮
      this.enableAddToCart();
      
      // 同步 Appstle
      this.syncWithAppstle(variantId, sellingPlanId);
      
      // 触发自定义事件
      this.dispatchEvent('subscriptionSelected', {
        variantId: variantId,
        sellingPlanId: sellingPlanId
      });
      
      // console.log('订阅已选择:', { variantId, sellingPlanId });
    }
    
    // 重置表单状态
    resetFormState() {
      if (!this.shopifyForm) {
        // 即使没有表单，也重置我们的按钮
        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.removeAttribute('disabled');
        }
        return;
      }
      
      // 移除可能阻止提交的属性
      const submitButtons = this.shopifyForm.querySelectorAll('button[type="submit"], button[name="add"]');
      submitButtons.forEach(btn => {
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      });
      
      // 也重置我们的按钮
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.removeAttribute('disabled');
      }
      
      // 清除表单的提交状态
      this.shopifyForm.removeAttribute('data-submitting');
      this.shopifyForm.removeAttribute('data-submitted');
      
      // 清除可能阻止提交的类名
      this.shopifyForm.classList.remove('submitting', 'submitted', 'disabled');
      
      // 确保表单可以再次提交
      // console.log('表单状态已重置，可以再次提交');
    }

    updateVisualFeedback(selectedRadio) {
      // 清除所有变体的所有选中状态
      this.container.querySelectorAll('.plan-label').forEach(label => {
        label.classList.remove('selected');
      });
      
      // 清除所有变体卡片的选中状态
      this.variantCards.forEach(card => {
        card.classList.remove('active-variant');
      });
      
      // 清除所有radio的checked状态（除了当前选中的）
      this.planRadios.forEach(radio => {
        if (radio !== selectedRadio) {
          radio.checked = false;
        }
      });
      
      // 添加选中状态到当前选项
      const selectedLabel = selectedRadio.closest('.plan-label');
      if (selectedLabel) {
        selectedLabel.classList.add('selected');
      }
      
      // 高亮对应的变体卡片
      const variantCard = selectedRadio.closest('.variant-subscription-card');
      if (variantCard) {
        variantCard.classList.add('active-variant');
      }
    }

    updatePriceDisplay(selectedRadio) {
      const planItem = selectedRadio.closest('.subscription-plan-item');
      if (!planItem) return;
      
      // 优先从 data-subscription-price 属性获取价格
      const planPricing = planItem.querySelector('.plan-pricing');
      let priceText = '';
      
      if (planPricing) {
        const priceElement = planPricing.querySelector('.price-current');
        if (priceElement) {
          // 尝试从 data 属性获取价格（更准确）
          const priceData = priceElement.getAttribute('data-subscription-price');
          if (priceData) {
            // 如果有 data 属性，使用它（已经是格式化的价格）
            priceText = priceElement.textContent.trim();
          } else {
            priceText = priceElement.textContent.trim();
          }
        }
      }
      
      // 如果没有找到，尝试从 Appstle 获取价格
      if (!priceText) {
        priceText = this.getPriceFromAppstle(selectedRadio);
      }
      
      // 更新购物车按钮价格显示
      if (this.cartPriceDisplay && priceText) {
        this.cartPriceDisplay.textContent = priceText;
      }
      
      // 更新全局价格显示（如果页面有）
      this.updateGlobalPriceDisplay(priceText);
    }
    
    // 从 Appstle 获取价格
    getPriceFromAppstle(radio) {
      if (!this.appstleInitialized) return null;
      
      try {
        // 查找 Appstle 小部件中的价格显示
        const appstleWidget = document.querySelector('.appstle_sub_widget');
        if (appstleWidget) {
          // 查找选中的订阅计划对应的价格
          const sellingPlanId = radio.dataset.sellingPlanId;
          if (sellingPlanId) {
            const appstlePlan = appstleWidget.querySelector(`input[value="${sellingPlanId}"]`);
            if (appstlePlan) {
              // 查找对应的价格显示元素
              const priceElement = appstlePlan.closest('label')?.querySelector('.price, .appstle-price, [data-price]');
              if (priceElement) {
                return priceElement.textContent.trim();
              }
            }
          }
        }
      } catch (error) {
        // console.warn('从 Appstle 获取价格失败:', error);
      }
      
      return null;
    }
    
    // 更新全局价格显示
    updateGlobalPriceDisplay(priceText) {
      if (!priceText) return;
      
      // 查找页面上的价格显示元素（常见的选择器）
      const priceSelectors = [
        '.product-price .price',
        '.product__price .price',
        '[data-product-price]',
        '.price--current',
        '.product-single__price'
      ];
      
      priceSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // 只更新主价格，不更新原价
          if (!el.closest('.price--compare') && !el.closest('.price-original')) {
            el.textContent = priceText;
          }
        });
      });
    }

    enableAddToCart() {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = false;
      }
    }

    setDefaultSelection() {
      // 默认选中第二个变体的第一个订阅选项（variant_index == 1）
      const secondCard = this.variantCards[1]; // 第二个变体卡片（索引为1）
      if (secondCard) {
        const firstPlanRadio = secondCard.querySelector('.plan-radio');
        if (firstPlanRadio) {
          // 如果还没有选中，设置为选中
          if (!firstPlanRadio.checked) {
            firstPlanRadio.checked = true;
          }
          // 触发选择处理
          this.handlePlanSelection(firstPlanRadio);
          // console.log('默认选中第二个变体的订阅选项');
        }
      } else {
        // 如果没有第二个变体，回退到第一个变体
        const firstCard = this.variantCards[0];
        if (firstCard) {
          const firstPlanRadio = firstCard.querySelector('.plan-radio');
          if (firstPlanRadio && !firstPlanRadio.checked) {
            firstPlanRadio.checked = true;
            this.handlePlanSelection(firstPlanRadio);
          }
        }
      }
      
      // 初始化一次性购买选择器（如果默认选择了第一个变体）
      if (this.oneTimeSelector && this.oneTimeSelector.selectedIndex > 0) {
        // 如果已经选择了变体（不是"请选择..."），触发价格显示
        const selectedIndex = this.oneTimeSelector.selectedIndex;
        if (selectedIndex > 0) {
          this.handleOneTimeVariantChange(this.oneTimeSelector);
        }
      }
    }

    readVariantFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      
      if (variantParam) {
        // 找到对应的变体卡片并高亮
        const variantCard = this.container.querySelector(`[data-variant-id="${variantParam}"]`);
        if (variantCard) {
          variantCard.classList.add('active-variant');
          variantCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          
          // 选择该变体的第一个订阅选项
          const firstPlanRadio = variantCard.querySelector('.plan-radio');
          if (firstPlanRadio) {
            firstPlanRadio.checked = true;
            this.handlePlanSelection(firstPlanRadio);
          }
        }
      }
    }

    watchPageVariantSelector() {
      // 监听页面上的变体选择器（如果有）
      const variantSelectors = document.querySelectorAll(
        'select[name="id"], input[name="id"][type="radio"]'
      );
      
      variantSelectors.forEach(selector => {
        selector.addEventListener('change', (e) => {
          const variantId = e.target.value;
          this.highlightVariantCard(variantId);
        });
      });
    }

    highlightVariantCard(variantId) {
      this.variantCards.forEach(card => {
        if (card.dataset.variantId === variantId) {
          card.classList.add('active-variant');
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          card.classList.remove('active-variant');
        }
      });
    }

    initAppstle() {
      // 检查 Appstle 是否可用
      if (typeof window.appstleInit === 'function') {
        try {
          window.appstleInit();
          this.appstleInitialized = true;
          // console.log('Appstle 已初始化');
        } catch (error) {
          // console.warn('Appstle 初始化失败:', error);
        }
      } else {
        // 等待 Appstle 加载
        this.waitForAppstle();
      }
      
      // 设置 Appstle 事件监听
      this.setupAppstleListeners();
    }

    waitForAppstle() {
      let attempts = 0;
      const maxAttempts = 20;
      
      const checkInterval = setInterval(() => {
        attempts++;
        
        if (typeof window.appstleInit === 'function') {
          clearInterval(checkInterval);
          this.initAppstle();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          // console.log('Appstle 未检测到，使用原生 Shopify 订阅功能');
        }
      }, 500);
    }

    setupAppstleListeners() {
      // 监听 Appstle 订阅计划选择
      document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', (event) => {
        // console.log('Appstle: 订阅计划已选择', event);
        this.handleAppstleSelection(event);
      });
      
      // 监听 Appstle 订阅计划取消
      document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', (event) => {
        // console.log('Appstle: 订阅计划已取消', event);
        this.handleAppstleDeselection(event);
      });
      
      // 监听 Appstle 价格更新事件（如果 Appstle 有提供）
      document.addEventListener('AppstleSubscription:PriceUpdated', (event) => {
        // console.log('Appstle: 价格已更新', event);
        if (event.detail && event.detail.sellingPlanId) {
          this.updatePriceFromAppstle(event.detail.sellingPlanId);
        }
      });
      
      // 定期检查 Appstle 价格更新（作为备用方案）
      if (this.appstleInitialized) {
        this.watchAppstlePriceChanges();
      }
    }
    
    // 监听 Appstle 价格变化
    watchAppstlePriceChanges() {
      let lastPrice = null;
      
      const checkPrice = () => {
        try {
          const appstleWidget = document.querySelector('.appstle_sub_widget');
          if (!appstleWidget) return;
          
          const selectedInput = appstleWidget.querySelector('input[name=selling_plan]:checked');
          if (selectedInput) {
            const planContainer = selectedInput.closest('label, .appstle-plan-item');
            if (planContainer) {
              const priceElement = planContainer.querySelector('.price, .appstle-price, [data-price]');
              if (priceElement) {
                const currentPrice = priceElement.textContent.trim();
                if (currentPrice !== lastPrice) {
                  lastPrice = currentPrice;
                  const sellingPlanId = selectedInput.value;
                  this.updatePriceFromAppstle(sellingPlanId);
                }
              }
            }
          }
        } catch (error) {
          // 静默处理错误
        }
      };
      
      // 仅在用户交互或订阅选择时检查价格变化，不再使用定期检查
      // 使用事件驱动而不是轮询，减少性能开销
      const priceObserver = new MutationObserver(() => {
        if (this.selectedSellingPlan) {
          checkPrice();
        }
      });
      
      if (appstleWidget) {
        priceObserver.observe(appstleWidget, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
      
      // 监听订阅选择变化
      document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', () => {
        setTimeout(checkPrice, 100);
      });
    }

    handleAppstleSelection(event) {
      // 从 Appstle 获取选中的销售计划
      const appstleInput = document.querySelector('.appstle_sub_widget input[name=selling_plan]:checked');
      if (appstleInput) {
        const sellingPlanId = appstleInput.value;
        const variantId = this.getVariantIdFromAppstle();
        
        // 同步到我们的界面
        const correspondingRadio = this.container.querySelector(
          `[data-variant-id="${variantId}"][data-selling-plan-id="${sellingPlanId}"]`
        );
        
        if (correspondingRadio) {
          correspondingRadio.checked = true;
          this.handlePlanSelection(correspondingRadio);
          
          // 从 Appstle 更新价格显示
          this.updatePriceFromAppstle(sellingPlanId);
        }
      }
    }
    
    // 从 Appstle 更新价格显示
    updatePriceFromAppstle(sellingPlanId) {
      try {
        const appstleWidget = document.querySelector('.appstle_sub_widget');
        if (!appstleWidget) return;
        
        // 查找 Appstle 中的价格显示
        const selectedPlan = appstleWidget.querySelector(`input[value="${sellingPlanId}"]`);
        if (selectedPlan) {
          const planContainer = selectedPlan.closest('label, .appstle-plan-item, [data-plan]');
          if (planContainer) {
            const priceElement = planContainer.querySelector('.price, .appstle-price, [data-price], .plan-price');
            if (priceElement) {
              const appstlePrice = priceElement.textContent.trim();
              
              // 更新我们的价格显示
              const correspondingPlan = this.container.querySelector(`[data-selling-plan-id="${sellingPlanId}"]`);
              if (correspondingPlan) {
                const ourPriceElement = correspondingPlan.querySelector('.price-current');
                if (ourPriceElement && ourPriceElement.textContent.trim() !== appstlePrice) {
                  ourPriceElement.textContent = appstlePrice;
                  ourPriceElement.setAttribute('data-subscription-price', appstlePrice);
                  
                  // 更新购物车按钮价格
                  if (this.cartPriceDisplay) {
                    this.cartPriceDisplay.textContent = appstlePrice;
                  }
                  
                  // console.log('已从 Appstle 更新价格:', appstlePrice);
                }
              }
            }
          }
        }
      } catch (error) {
        // console.warn('从 Appstle 更新价格失败:', error);
      }
    }

    handleAppstleDeselection(event) {
      // 处理取消选择
      // console.log('Appstle: 订阅计划已取消选择');
    }

    syncWithAppstle(variantId, sellingPlanId) {
      if (!this.appstleInitialized || !sellingPlanId) return;
      
      // 同步到 Appstle 小部件
      const appstleInput = document.querySelector(`.appstle_sub_widget input[value="${sellingPlanId}"]`);
      if (appstleInput) {
        appstleInput.checked = true;
        
        // 触发 change 事件
        const changeEvent = new Event('change', { bubbles: true });
        appstleInput.dispatchEvent(changeEvent);
      }
    }

    getVariantIdFromAppstle() {
      // 尝试从 Appstle 小部件获取变体 ID
      const variantInput = document.querySelector('.appstle_sub_widget input[name="id"]');
      return variantInput ? variantInput.value : this.selectedVariant;
    }

    addToCart() {
      if (!this.selectedVariant) {
        alert('请先选择一个订阅计划或一次性购买选项');
        return;
      }
      
      // console.log('准备添加到购物车:', {
      //   variant: this.selectedVariant,
      //   selling_plan: this.selectedSellingPlan || '(一次性购买)'
      // });
      
      // 重置表单状态（重要：确保可以再次提交）
      this.resetFormState();
      
      // 如果选择了订阅计划，先移除折扣码
      if (this.selectedSellingPlan) {
        this.removeDiscountCodeBeforeAdd();
      }
      
      // 确保表单已同步（多次同步确保值正确）
      this.syncToShopifyForm(this.selectedVariant, this.selectedSellingPlan);
      
      // 清理表单，移除可能冲突的字段
      this.cleanupFormForSubscription();
      
      // 再次验证并设置 selling_plan（确保值已设置）
      this.ensureSellingPlanInForm();
      
      // 直接使用 Shopify 原生按钮（我们的按钮已隐藏）
      if (this.shopifyAddToCartBtn) {
        // 确保按钮可用
        this.shopifyAddToCartBtn.disabled = false;
        this.shopifyAddToCartBtn.removeAttribute('disabled');
        
        // 延迟一小段时间，确保表单字段已更新
        setTimeout(() => {
          // 再次验证 selling_plan 是否已设置
          this.verifySellingPlanBeforeSubmit();
          
          // 最终清理，确保没有重复字段
          this.cleanupFormForSubscription();
          
          // 最终验证表单数据
          if (this.shopifyForm) {
            const formData = new FormData(this.shopifyForm);
            const finalVariantId = formData.get('id');
            const finalSellingPlan = formData.get('selling_plan');
            
            // console.log('最终表单数据:', {
            //   id: finalVariantId,
            //   selling_plan: finalSellingPlan || '(空，一次性购买)',
            //   quantity: formData.get('quantity'),
            //   expected_variant: this.selectedVariant,
            //   expected_selling_plan: this.selectedSellingPlan || '(空，一次性购买)'
            // });
            
            // 如果数据不匹配，强制修复
            if (finalVariantId !== this.selectedVariant) {
              // console.warn('变体ID不匹配，正在修复...');
              const variantInput = this.shopifyForm.querySelector('[name="id"]');
              if (variantInput) {
                variantInput.value = this.selectedVariant;
              }
            }
            
            if (this.selectedSellingPlan && finalSellingPlan !== this.selectedSellingPlan) {
              // console.warn('销售计划ID不匹配，正在修复...');
              const sellingPlanInput = this.shopifyForm.querySelector('[name="selling_plan"]');
              if (sellingPlanInput) {
                sellingPlanInput.value = this.selectedSellingPlan;
              }
            }
            
            // 如果没有选择订阅，确保 selling_plan 为空
            if (!this.selectedSellingPlan) {
              const sellingPlanInput = this.shopifyForm.querySelector('[name="selling_plan"]');
              if (sellingPlanInput && sellingPlanInput.value) {
                // console.log('清除 selling_plan（一次性购买）');
                sellingPlanInput.value = '';
              }
            }
          }
          
          // 检查是否正在提交（防止重复提交）
          if (this.shopifyForm && this.shopifyForm.getAttribute('data-submitting') === 'true') {
            // console.warn('表单正在提交中，请稍候...');
            return;
          }
          
          // 标记表单正在提交（防止重复提交）
          if (this.shopifyForm) {
            this.shopifyForm.setAttribute('data-submitting', 'true');
          }
          
          // 临时禁用按钮（防止重复点击）
          const originalDisabled = this.shopifyAddToCartBtn.disabled;
          this.shopifyAddToCartBtn.disabled = true;
          
          // 触发原生按钮的点击事件
          try {
            this.shopifyAddToCartBtn.click();
            // console.log('使用 Shopify 原生加购按钮', {
            //   variant: this.selectedVariant,
            //   selling_plan: this.selectedSellingPlan || '(一次性购买)'
            // });
            
            // 延迟重置提交状态和按钮
            setTimeout(() => {
              // 恢复按钮状态
              this.shopifyAddToCartBtn.disabled = originalDisabled;
              
              if (this.shopifyForm) {
                this.shopifyForm.removeAttribute('data-submitting');
                this.shopifyForm.removeAttribute('data-submitted');
              }
              
              // 重置表单状态，确保可以下次提交
              this.resetFormState();
              
              // console.log('表单提交完成，已重置状态，可以再次提交');
            }, 800); // 增加延迟，确保第一次提交完成
            
            // 更长的延迟，确保完全重置
            setTimeout(() => {
              this.resetFormState();
            }, 2000);
          } catch (error) {
            // console.error('点击加购按钮失败:', error);
            // 重置状态
            this.shopifyAddToCartBtn.disabled = originalDisabled;
            this.resetFormState();
          }
          
          // 添加到购物车后，检查并移除折扣码（只调用一次，减少请求）
          if (this.selectedSellingPlan) {
            // 延迟移除，确保商品已添加到购物车
            setTimeout(() => {
              this.forceRemoveDiscountCode();
            }, 500);
          }
          
          // 验证购物车是否更新（订阅和一次性购买都需要）
          setTimeout(() => {
            this.verifyCartUpdate();
          }, 500);
        }, 150); // 增加延迟时间，确保所有字段都已更新
        return;
      }
      
      // 如果没有原生按钮，使用 API 方式
      const formData = {
        id: this.selectedVariant,
        quantity: 1
      };
      
      if (this.selectedSellingPlan) {
        formData.selling_plan = this.selectedSellingPlan;
      }
      
      // 使用 Shopify Cart API 添加到购物车
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })
      .then(response => response.json())
      .then(data => {
        // console.log('已添加到购物车:', data);
        
        // 触发购物车更新事件（Shopify 主题通常会监听这个事件）
        document.dispatchEvent(new CustomEvent('cart:updated'));
        window.dispatchEvent(new CustomEvent('cart:updated'));
        
        // 触发购物车更新事件
        this.dispatchEvent('cartUpdated', data);
        
        // 更新购物车图标（如果页面有）
        this.updateCartCount();
      })
      .catch(error => {
        // console.error('添加到购物车失败:', error);
        alert('添加到购物车失败，请重试');
      });
    }
    
    // 查找 Shopify 原生产品表单
    findShopifyProductForm() {
      // 尝试多种可能的选择器
      const selectors = [
        'form[action="/cart/add"]',
        'form[action*="/cart/add"]',
        'form.product-form',
        '[data-product-form]',
        'form[action^="/cart"]'
      ];
      
      for (const selector of selectors) {
        const form = document.querySelector(selector);
        if (form) {
          // console.log('找到 Shopify 产品表单:', selector);
          return form;
        }
      }
      
      // console.warn('未找到 Shopify 产品表单');
      return null;
    }
    
    // 查找 Shopify 原生变体输入
    findShopifyVariantInput() {
      if (!this.shopifyForm) return null;
      
      const selectors = [
        'input[name="id"]',
        'select[name="id"]',
        '[name="id"]'
      ];
      
      for (const selector of selectors) {
        const input = this.shopifyForm.querySelector(selector);
        if (input) {
          // console.log('找到 Shopify 变体输入:', selector);
          return input;
        }
      }
      
      // 如果表单内没找到，在整个页面查找
      for (const selector of selectors) {
        const input = document.querySelector(selector);
        if (input) {
          // console.log('在页面找到 Shopify 变体输入:', selector);
          return input;
        }
      }
      
      // console.warn('未找到 Shopify 变体输入');
      return null;
    }
    
    // 查找 Shopify 原生销售计划输入
    findShopifySellingPlanInput() {
      if (!this.shopifyForm) {
        // 即使没有表单，也在整个页面查找
        const selectors = [
          'input[name="selling_plan"]',
          'select[name="selling_plan"]',
          '[name="selling_plan"]',
          'input[name="selling_plan_id"]',
          '[name="selling_plan_id"]'
        ];
        
        for (const selector of selectors) {
          const input = document.querySelector(selector);
          if (input) {
            // console.log('在页面找到 Shopify 销售计划输入:', selector);
            return input;
          }
        }
        return null;
      }
      
      const selectors = [
        'input[name="selling_plan"]',
        'select[name="selling_plan"]',
        '[name="selling_plan"]',
        'input[name="selling_plan_id"]',
        '[name="selling_plan_id"]'
      ];
      
      for (const selector of selectors) {
        const input = this.shopifyForm.querySelector(selector);
        if (input) {
          // console.log('找到 Shopify 销售计划输入:', selector);
          return input;
        }
      }
      
      // 如果表单内没找到，在整个页面查找
      for (const selector of selectors) {
        const input = document.querySelector(selector);
        if (input) {
          // console.log('在页面找到 Shopify 销售计划输入:', selector);
          return input;
        }
      }
      
      // 如果没有找到，稍后会在 ensureSellingPlanInForm 中创建
      // console.log('未找到 Shopify 销售计划输入，将在需要时创建');
      return null;
    }
    
    // 查找 Shopify 原生加购按钮
    findShopifyAddToCartButton() {
      if (!this.shopifyForm) {
        // 即使没有表单，也尝试查找按钮
        const selectors = [
          'button[type="submit"][name="add"]',
          'button[name="add"]',
          '[data-add-to-cart]',
          '.product-form__cart-submit',
          'button[type="submit"]'
        ];
        
        for (const selector of selectors) {
          const btn = document.querySelector(selector);
          if (btn && (btn.closest('form') || btn.textContent.toLowerCase().includes('add') || btn.textContent.toLowerCase().includes('cart'))) {
            // console.log('找到 Shopify 加购按钮:', selector);
            return btn;
          }
        }
        return null;
      }
      
      const selectors = [
        'button[type="submit"][name="add"]',
        'button[name="add"]',
        '[data-add-to-cart]',
        '.product-form__cart-submit',
        'button[type="submit"]'
      ];
      
      for (const selector of selectors) {
        const btn = this.shopifyForm.querySelector(selector);
        if (btn) {
          // console.log('在表单中找到 Shopify 加购按钮:', selector);
          return btn;
        }
      }
      
      // 如果表单内没找到，在整个页面查找
      for (const selector of selectors) {
        const btn = document.querySelector(selector);
        if (btn) {
          // console.log('在页面找到 Shopify 加购按钮:', selector);
          return btn;
        }
      }
      
      // console.warn('未找到 Shopify 加购按钮');
      return null;
    }
    
    // 同步选择到 Shopify 原生表单
    syncToShopifyForm(variantId, sellingPlanId) {
      // 更新变体输入
      if (this.shopifyVariantInput) {
        if (this.shopifyVariantInput.tagName === 'SELECT') {
          this.shopifyVariantInput.value = variantId;
        } else {
          this.shopifyVariantInput.value = variantId;
        }
        
        // 触发 change 事件，让其他监听器知道变体已改变
        const changeEvent = new Event('change', { bubbles: true });
        this.shopifyVariantInput.dispatchEvent(changeEvent);
      }
      
      // 更新销售计划输入
      if (this.shopifySellingPlanInput) {
        this.shopifySellingPlanInput.value = sellingPlanId || '';
        
        // 触发 change 事件
        const changeEvent = new Event('change', { bubbles: true });
        this.shopifySellingPlanInput.dispatchEvent(changeEvent);
      }
      
      // 确保 selling_plan 字段存在（如果不存在则创建）
      this.ensureSellingPlanInForm();
      
      // console.log('已同步到 Shopify 表单:', { variantId, sellingPlanId });
    }
    
    // 确保 selling_plan 字段在表单中
    ensureSellingPlanInForm() {
      if (!this.shopifyForm) return;
      
      // 移除所有重复的 selling_plan 字段（避免冲突）
      const existingSellingPlanInputs = this.shopifyForm.querySelectorAll('[name="selling_plan"], [name="selling_plan_id"]');
      existingSellingPlanInputs.forEach((input, index) => {
        if (index > 0) {
          // 保留第一个，移除其他的
          input.remove();
          // console.log('移除了重复的 selling_plan 字段');
        }
      });
      
      // 检查是否存在 selling_plan 输入
      let sellingPlanInput = this.shopifyForm.querySelector('[name="selling_plan"]');
      
      // 如果不存在，创建它
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        this.shopifyForm.appendChild(sellingPlanInput);
        // console.log('创建了 selling_plan 输入字段');
      }
      
      // 设置值
      if (this.selectedSellingPlan) {
        sellingPlanInput.value = this.selectedSellingPlan;
        // console.log('设置 selling_plan 值:', this.selectedSellingPlan);
      } else {
        // 如果没有选择订阅，清空该字段（不要移除，避免表单结构变化）
        sellingPlanInput.value = '';
      }
      
      // 更新引用
      this.shopifySellingPlanInput = sellingPlanInput;
      
      // 移除 selling_plan_id（避免冲突，只使用 selling_plan）
      const sellingPlanIdInput = this.shopifyForm.querySelector('[name="selling_plan_id"]');
      if (sellingPlanIdInput) {
        sellingPlanIdInput.remove();
        // console.log('移除了 selling_plan_id 字段，只使用 selling_plan');
      }
    }
    
    // 在提交前验证 selling_plan
    verifySellingPlanBeforeSubmit() {
      if (!this.shopifyForm) {
        // console.warn('未找到 Shopify 表单');
        return;
      }
      
      const sellingPlanInput = this.shopifyForm.querySelector('[name="selling_plan"]');
      const variantInput = this.shopifyForm.querySelector('[name="id"]');
      
      // 验证变体ID
      if (variantInput) {
        if (variantInput.value !== this.selectedVariant) {
          // console.warn('变体ID不匹配，正在修复...', {
          //   expected: this.selectedVariant,
          //   actual: variantInput.value
          // });
          variantInput.value = this.selectedVariant;
          
          // 如果是 SELECT，也需要更新
          if (variantInput.tagName === 'SELECT') {
            variantInput.value = this.selectedVariant;
            // 触发 change 事件
            const changeEvent = new Event('change', { bubbles: true });
            variantInput.dispatchEvent(changeEvent);
          }
        }
      } else {
        // console.warn('未找到变体输入字段');
      }
      
      // 验证销售计划
      if (this.selectedSellingPlan) {
        if (!sellingPlanInput) {
          // console.warn('未找到 selling_plan 输入字段，正在创建...');
          this.ensureSellingPlanInForm();
          return;
        }
        
        if (sellingPlanInput.value !== this.selectedSellingPlan) {
          // console.warn('selling_plan 值不匹配，正在修复...', {
          //   expected: this.selectedSellingPlan,
          //   actual: sellingPlanInput.value
          // });
          sellingPlanInput.value = this.selectedSellingPlan;
          
          // 触发 change 事件
          const changeEvent = new Event('change', { bubbles: true });
          sellingPlanInput.dispatchEvent(changeEvent);
        }
        
        // console.log('验证 selling_plan:', {
        //   expected: this.selectedSellingPlan,
        //   actual: sellingPlanInput.value,
        //   variant: variantInput ? variantInput.value : 'not found'
        // });
      } else {
        // 一次性购买，确保 selling_plan 为空
        if (sellingPlanInput && sellingPlanInput.value) {
          // console.log('一次性购买：清除 selling_plan 值');
          sellingPlanInput.value = '';
        }
      }
    }
    
    // 验证购物车是否更新
    verifyCartUpdate() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // console.log('购物车验证:', {
          //   item_count: cart.item_count,
          //   items: cart.items.map(item => ({
          //     variant_id: item.variant_id,
          //     selling_plan: item.selling_plan,
          //     title: item.product_title
          //   }))
          // });
          
          // 检查是否有我们刚添加的商品
          const hasOurItem = cart.items.some(item => {
            const matchesVariant = item.variant_id.toString() === this.selectedVariant.toString();
            if (this.selectedSellingPlan) {
              // 订阅商品，需要检查 selling_plan
              return matchesVariant && item.selling_plan && 
                     item.selling_plan.toString() === this.selectedSellingPlan.toString();
            } else {
              // 一次性购买，不应该有 selling_plan
              return matchesVariant && !item.selling_plan;
            }
          });
          
          if (!hasOurItem) {
            // console.warn('购物车中没有找到刚添加的商品，可能添加失败');
            // console.warn('预期:', {
            //   variant: this.selectedVariant,
            //   selling_plan: this.selectedSellingPlan || '(一次性购买)'
            // });
          } else {
            // console.log('✅ 购物车更新成功');
          }
        })
        .catch(error => {
          // console.error('验证购物车更新失败:', error);
        });
    }

    showSuccessMessage() {
      // 创建成功提示
      const message = document.createElement('div');
      message.className = 'add-to-cart-success';
      message.textContent = '✓ 已添加到购物车';
      message.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #28a745;
        color: white;
        padding: 1rem 2rem;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        z-index: 10000;
        animation: slideIn 0.3s ease;
      `;
      
      document.body.appendChild(message);
      
      setTimeout(() => {
        message.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => message.remove(), 300);
      }, 2000);
    }

    updateCartCount() {
      // 更新购物车数量（如果页面有购物车图标）
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // 更新各种可能的购物车数量元素
          const cartCountSelectors = [
            '[data-cart-count]',
            '.cart-count',
            '#cart-count',
            '[aria-label*="cart"] .count',
            '.header__cart-count'
          ];
          
          cartCountSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              el.textContent = cart.item_count;
              // 触发更新事件
              el.dispatchEvent(new Event('cart:updated'));
            });
          });
          
          // 触发全局购物车更新事件
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(error => {
          // console.error('更新购物车数量失败:', error);
        });
    }

    dispatchEvent(eventName, detail) {
      const event = new CustomEvent(`wonderbioticsSubscriptions:${eventName}`, {
        detail: detail,
        bubbles: true
      });
      this.container.dispatchEvent(event);
    }

    // 公共方法：获取当前选择
    getSelection() {
      return {
        variantId: this.selectedVariant,
        sellingPlanId: this.selectedSellingPlan
      };
    }

    // 公共方法：设置选择
    setSelection(variantId, sellingPlanId = '') {
      const radio = this.container.querySelector(
        `[data-variant-id="${variantId}"][data-selling-plan-id="${sellingPlanId}"]`
      );
      
      if (radio) {
        radio.checked = true;
        this.handlePlanSelection(radio);
        return true;
      }
      
      return false;
    }
    
    // 监听购物车更新，检测并移除自动应用的折扣码
    watchCartForAutoDiscounts() {
      // 监听购物车更新事件（添加节流，避免频繁调用）
      let cartUpdateTimeout = null;
      const handleCartUpdate = () => {
        if (cartUpdateTimeout) clearTimeout(cartUpdateTimeout);
        cartUpdateTimeout = setTimeout(() => {
          if (this.selectedSellingPlan) {
            this.checkAndRemoveAutoDiscounts();
          }
        }, 500); // 节流：500ms内只执行一次
      };
      
      document.addEventListener('cart:updated', handleCartUpdate);
      document.addEventListener('cart:refresh', handleCartUpdate);
      
      // 监听购物车弹窗打开（优化：添加节流和防抖）
      const cartDrawer = document.querySelector('[data-cart-drawer], .cart-drawer, #cart-drawer, [data-cart-sidebar]');
      if (cartDrawer) {
        let mutationTimeout = null;
        const observer = new MutationObserver(() => {
          if (mutationTimeout) clearTimeout(mutationTimeout);
          mutationTimeout = setTimeout(() => {
            if (this.selectedSellingPlan) {
              this.removeDiscountFromDOM(); // 只从DOM移除，不调用API
            }
          }, 300); // 防抖：300ms内只执行一次
        });
        
        observer.observe(cartDrawer, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }
      
      // 监听购物车按钮点击（打开购物车弹窗）
      const cartButtons = document.querySelectorAll('[data-cart-toggle], .cart-toggle, [aria-controls*="cart"]');
      let cartButtonTimeout = null;
      cartButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (cartButtonTimeout) clearTimeout(cartButtonTimeout);
          cartButtonTimeout = setTimeout(() => {
            if (this.selectedSellingPlan) {
              this.removeDiscountFromDOM(); // 只从DOM移除，减少API调用
            }
          }, 500);
        });
      });
      
      // 定期检查（每10秒检查一次，仅在页面可见且有订阅计划时）
      // 使用 requestIdleCallback 或 setTimeout 来减少性能影响
      this.cartWatchInterval = null;
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => {
          this.startCartWatch();
        });
      } else {
        setTimeout(() => {
          this.startCartWatch();
        }, 5000);
      }
    }
    
    // 在添加到购物车前移除折扣码
    removeDiscountCodeBeforeAdd() {
      // 先检查当前购物车是否有折扣码
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const discountCodes = cart.discount_codes || [];
          if (discountCodes.length > 0) {
            // console.log('检测到购物车中有折扣码，订阅商品将移除折扣码:', discountCodes);
            // 移除所有折扣码
            this.forceRemoveDiscountCode();
          }
        })
        .catch(error => {
          // console.error('检查购物车折扣码失败:', error);
        });
    }
    
    // 强制移除折扣码
    forceRemoveDiscountCode() {
      // console.log('强制移除折扣码（订阅商品不应有折扣码）');
      
      // 同时使用多种方法移除
      this.removeDiscountFromDOM(); // 先从 DOM 移除显示
      
      // 方法1：通过 API 移除
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: ''
        })
      })
      .then(response => response.json())
      .then(data => {
        // console.log('已通过 API 移除折扣码，响应:', data);
        
        // 再次从 DOM 移除（确保）
        this.removeDiscountFromDOM();
        
        // 触发购物车更新
        document.dispatchEvent(new CustomEvent('cart:updated'));
        window.dispatchEvent(new CustomEvent('cart:updated'));
        
        // 刷新购物车显示
        if (typeof window.theme?.cart?.refresh === 'function') {
          window.theme.cart.refresh();
        } else if (typeof window.theme?.cart?.drawer?.refresh === 'function') {
          window.theme.cart.drawer.refresh();
        }
        
        // 触发购物车更新事件（不重复获取购物车数据）
        const event = new CustomEvent('cart:updated', { detail: data });
        document.dispatchEvent(event);
        window.dispatchEvent(event);
        
        // 只检查一次，减少请求次数
        if (!this.discountRemovedVerified) {
          this.discountRemovedVerified = true;
          setTimeout(() => {
            this.verifyDiscountRemoved();
            this.removeDiscountFromDOM();
            // 重置标志，允许下次再次验证
            setTimeout(() => {
              this.discountRemovedVerified = false;
            }, 1000);
          }, 500);
        }
      })
      .catch(error => {
        // console.error('通过 API 移除折扣码失败:', error);
        // 如果 API 失败，尝试其他方法
        this.removeDiscountCodeAlternative();
        this.removeDiscountFromDOM();
      });
    }
    
    // 从 DOM 中移除折扣码显示
    removeDiscountFromDOM() {
      // 查找所有可能显示折扣码的元素
      const selectors = [
        '.cart-discount',
        '.discount-code',
        '[data-discount-code]',
        '.cart__discount',
        '.cart-item__discount',
        '.cart-discount-code',
        '[class*="discount"]',
        '[class*="coupon"]',
        '[id*="discount"]',
        '[id*="coupon"]'
      ];
      
      let removed = false;
      
      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            const text = el.textContent || '';
            // 检查是否包含折扣码相关文本
            if (text.includes('NEWCUSTOMER10') || 
                text.includes('NEWCUSTOMER') || 
                text.includes('10%') ||
                (text.includes('discount') && text.length < 50) ||
                (text.includes('coupon') && text.length < 50)) {
              // console.log('从 DOM 移除折扣码元素:', text);
              el.remove();
              removed = true;
            }
          });
        } catch (e) {
          // 忽略选择器错误
        }
      });
      
      // 优化：只在购物车相关区域查找，而不是所有元素
      const cartAreas = document.querySelectorAll('[data-cart-drawer], .cart-drawer, #cart-drawer, [data-cart-sidebar], .cart, [class*="cart"]');
      cartAreas.forEach(area => {
        const discountElements = area.querySelectorAll('*');
        discountElements.forEach(el => {
          const text = el.textContent || '';
          if (text.includes('NEWCUSTOMER10') && el.children.length === 0) {
            const parent = el.parentElement;
            if (parent && parent.textContent.includes('NEWCUSTOMER10')) {
              parent.remove();
              removed = true;
            }
          }
        });
      });
      
      if (removed) {
        // console.log('已从 DOM 移除折扣码显示');
      }
    }
    
    // 备用方法：移除折扣码
    removeDiscountCodeAlternative() {
      // 方法2：查找并移除折扣码输入框
      const discountInputs = document.querySelectorAll(
        'input[name="discount"], ' +
        'input[name="discount_code"], ' +
        'input[name="coupon"], ' +
        '[data-discount-input]'
      );
      
      discountInputs.forEach(input => {
        if (input.value) {
          // console.log('找到折扣码输入框，清空:', input.value);
          input.value = '';
        }
      });
      
      // 方法3：查找并移除折扣码显示元素
      const discountDisplays = document.querySelectorAll(
        '.cart-discount, ' +
        '.discount-code, ' +
        '[data-discount-code], ' +
        '.cart__discount'
      );
      
      discountDisplays.forEach(display => {
        const code = display.textContent;
        if (code && (code.includes('NEWCUSTOMER10') || code.includes('10'))) {
          // console.log('找到折扣码显示元素，移除:', code);
          display.remove();
        }
      });
      
      // 方法4：通过 URL 参数移除（如果有）
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('discount') || urlParams.has('coupon')) {
        urlParams.delete('discount');
        urlParams.delete('coupon');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
        // console.log('已从 URL 移除折扣码参数');
      }
    }
    
    // 验证折扣码是否已移除
    verifyDiscountRemoved() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const discountCodes = cart.discount_codes || [];
          if (discountCodes.length > 0) {
            // console.warn('折扣码仍然存在，再次尝试移除:', discountCodes);
            this.forceRemoveDiscountCode();
            this.removeDiscountFromDOM();
            
            // 如果仍然存在，尝试更激进的方法
            setTimeout(() => {
              this.aggressiveRemoveDiscount();
            }, 1000);
          } else {
            // console.log('✅ 折扣码已成功移除');
            // 即使 API 说已移除，也要确保 DOM 中没有显示
            this.removeDiscountFromDOM();
          }
        })
        .catch(error => {
          // console.error('验证折扣码移除失败:', error);
          // 即使验证失败，也尝试从 DOM 移除
          this.removeDiscountFromDOM();
        });
    }
    
    // 更激进的方法移除折扣码
    aggressiveRemoveDiscount() {
      // console.log('使用激进方法移除折扣码');
      
      // 方法1：直接修改购物车数据
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: null,
          discount_code: '',
          discount_codes: []
        })
      })
      .then(response => response.json())
      .then(data => {
        // console.log('激进方法移除折扣码响应:', data);
        this.removeDiscountFromDOM();
      })
      .catch(error => {
        // console.error('激进方法失败:', error);
      });
      
      // 方法2：查找并移除所有折扣相关元素
      const discountElements = document.querySelectorAll('*');
      discountElements.forEach(el => {
        const text = el.textContent || '';
        const className = el.className || '';
        const id = el.id || '';
        
        if ((text.includes('NEWCUSTOMER10') || 
             text.includes('NEWCUSTOMER') ||
             (className.includes('discount') && text.includes('10'))) &&
            el.children.length === 0) {
          // console.log('激进移除折扣元素:', text);
          el.style.display = 'none';
          el.remove();
        }
      });
    }
    
    // 启动购物车监控（仅在需要时）
    startCartWatch() {
      // 只在页面可见且有订阅计划时启动
      if (!this.selectedSellingPlan || document.hidden) {
        return;
      }
      
      // 清除之前的定时器
      if (this.cartWatchInterval) {
        clearInterval(this.cartWatchInterval);
      }
      
      // 每10秒检查一次（从2秒改为10秒）
      this.cartWatchInterval = setInterval(() => {
        // 只在页面可见时检查
        if (!document.hidden && this.selectedSellingPlan) {
          // 节流：5秒内最多检查一次
          const now = Date.now();
          if (now - this.lastCartCheck > this.cartCheckThrottle) {
            this.lastCartCheck = now;
            this.checkAndRemoveAutoDiscounts();
            this.removeDiscountFromDOM();
          }
        }
      }, 10000); // 从2秒改为10秒
    }
    
    // 检查并移除自动应用的折扣码（优化：添加节流）
    checkAndRemoveAutoDiscounts() {
      // 节流：避免频繁请求
      const now = Date.now();
      if (now - this.lastCartCheck < this.cartCheckThrottle) {
        return;
      }
      this.lastCartCheck = now;
      
      // 检查购物车中是否有订阅商品
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // 检查是否有订阅商品（有 selling_plan）
          const hasSubscription = cart.items.some(item => item.selling_plan);
          
          if (hasSubscription) {
            // 检查是否有折扣码（无论什么折扣码，订阅商品都不应该有）
            const discountCodes = cart.discount_codes || [];
            
            if (discountCodes.length > 0) {
              // 移除所有折扣码
              this.forceRemoveDiscountCode();
            }
          }
        })
        .catch(error => {
          // 静默处理错误，避免控制台噪音
        });
    }
    
    // 移除折扣码（保留此方法以兼容旧代码）
    removeDiscountCodes(codes) {
      // 直接调用强制移除方法
      this.forceRemoveDiscountCode();
    }
    
    // 阻止自动应用折扣码（在添加到购物车时）
    preventAutoDiscountOnAdd() {
      // 监听所有可能自动应用折扣码的事件
      const preventAutoDiscount = (e) => {
        if (this.selectedSellingPlan) {
          // 如果有订阅计划，阻止自动应用折扣码
          // console.log('检测到订阅计划，阻止自动应用折扣码');
          
          // 延迟检查并移除
          setTimeout(() => {
            this.checkAndRemoveAutoDiscounts();
          }, 1000);
        }
      };
      
      // 监听各种可能触发自动折扣的事件
      document.addEventListener('cart:add', preventAutoDiscount);
      document.addEventListener('cart:updated', preventAutoDiscount);
      
      // 如果使用 Shopify 原生按钮，在点击后检查
      if (this.shopifyAddToCartBtn) {
        const originalClick = this.shopifyAddToCartBtn.onclick;
        this.shopifyAddToCartBtn.addEventListener('click', () => {
          if (this.selectedSellingPlan) {
            setTimeout(() => {
              this.checkAndRemoveAutoDiscounts();
            }, 1500);
          }
        });
      }
    }
  }

  // 初始化
  document.addEventListener('DOMContentLoaded', function() {
    const containers = document.querySelectorAll('.wonderbiotics-subscriptions-wrapper');
    
    containers.forEach(container => {
      const instance = new WonderbioticsSubscriptions(container);
      container.wonderbioticsSubscriptions = instance;
    });
    
    // console.log('Wonderbiotics 订阅组件已初始化', containers.length, '个实例');
    
    /* ======================================
     * One-time purchase 链接 — Add to cart 按钮下方
     * Added: 2026-04-14
     * 功能：在 Add to cart 按钮下方插入一个 "One-time purchase" 链接，
     *       点击后将第一个变体加入购物车并跳转到 /cart 页面。
     *       不修改现有任何逻辑，纯增量添加。
     * ====================================== */
    (function initOneTimePurchaseLink() {
      // 检查订阅组件容器是否存在
      const wrapper = document.querySelector('.wonderbiotics-subscriptions-wrapper');
      if (!wrapper) return;

      // 从已隐藏的 One-time purchase 下拉框读取第一个变体信息
      const selector = document.getElementById('one-time-variant-selector');
      if (!selector || !selector.options || selector.options.length === 0) return;

      const firstOption = selector.options[0];
      const variantId = firstOption.value;               // 第一个变体 ID
      const variantPrice = firstOption.dataset.variantPrice || ''; // 格式化价格，如 "$55.40"

      // 找到 Add to cart 按钮容器，作为链接的插入锚点
      const btnContainer = document.querySelector('.product-form__buttons');
      if (!btnContainer) return;

      // 创建链接元素并设置无障碍属性
      const link = document.createElement('a');
      link.href = '/cart';
      link.className = 'one-time-purchase-link';
      link.textContent = 'Buy Once for ' + variantPrice + ' + $6 Shipping fee';
      link.dataset.variantId = variantId;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-label', 'Buy once for ' + variantPrice + ' plus 6 dollars shipping, add to cart and go to checkout');

      // 插入到 Add to cart 按钮下方
      btnContainer.parentNode.insertBefore(link, btnContainer.nextSibling);

      // 链接下方添加临床提示文字，关联到链接供屏幕阅读器补充说明
      const hintId = 'one-time-purchase-hint';
      const hint = document.createElement('p');
      hint.className = 'one-time-purchase-hint';
      hint.id = hintId;
      hint.setAttribute('role', 'note');
      hint.textContent = 'Clinically proven results start to show between 3 and 6 months.';
      link.parentNode.insertBefore(hint, link.nextSibling);

      // 将提示文字关联到链接，屏幕阅读器会在读完链接后补充读出提示内容
      link.setAttribute('aria-describedby', hintId);

      // 点击事件：加购第一个变体 → 跳转购物车
      link.addEventListener('click', function(e) {
        e.preventDefault();
        const vid = this.dataset.variantId;
        if (!vid) return;

        // 防止重复点击
        if (this.getAttribute('aria-disabled') === 'true') return;
        this.setAttribute('aria-disabled', 'true');
        this.style.opacity = '0.5';
        this.style.pointerEvents = 'none';

        // 调用 Shopify Cart API 加购（不带 selling_plan = 一次性购买）
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(vid, 10), quantity: 1 })
        })
        .then(function(res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function() {
          // 加购成功，跳转到购物车页面
          window.location.href = '/cart';
        })
        .catch(function(err) {
          // 加购失败，恢复链接状态允许重试
          console.error('One-time purchase add to cart failed:', err);
          link.setAttribute('aria-disabled', 'false');
          link.style.opacity = '';
          link.style.pointerEvents = '';
        });
      });
    })();
  });

  // 导出到全局
  window.WonderbioticsSubscriptions = WonderbioticsSubscriptions;

})();

